import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Switch,
  TextField,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from "@mui/material";
import Payment from "./Payment"; // Assuming the Payment component is in the same directory

function MakeReceipt({
  orders,
  setOrders,
  tableId,
  orderId,
  customer,
  vatRate = 0.13,
}) {
  const API_BASE_URL = process.env.REACT_APP_BASE_URL;
  const { slug } = useParams();
  console.log("orders", orders);
  const [formData, setFormData] = useState({
    table_id: null,
    invoiceId: null,
    isDiscountOn: false,
    discountPercent: 0,
    isServiceChargeOn: false,
    serviceChargePercent: 0,
    openPaymentDialog: false,
  });

  useEffect(() => {
    if (orders && orders.length > 0) {
      setFormData((prevState) => ({
        ...prevState,
        invoiceId: orders[0]?.invoice?.id,
        table_id: orders[0]?.table_id,
      }));
    }
  }, [orders]);

  const totalPax = orders.reduce((totalPax, order) => {
    const orderPax = order.order_items.reduce(
      (itemSum, item) => itemSum + item.quantity,
      0
    );
    return totalPax + orderPax;
  }, 0);

  const calculateSubTotal = () => {
    return orders
      .reduce((total, order) => {
        return (
          total +
          order.order_items.reduce(
            (sum, item) => sum + item.quantity * item.price,
            0
          )
        );
      }, 0)
      .toFixed(2);
  };

  const calculateVat = (amount) => {
    return (amount * vatRate).toFixed(2);
  };

  const calculateDiscount = (subTotal) => {
    return formData.isDiscountOn
      ? ((subTotal * formData.discountPercent) / 100).toFixed(2)
      : 0;
  };

  const calculateServiceCharge = (subTotal) => {
    return formData.isServiceChargeOn
      ? ((subTotal * formData.serviceChargePercent) / 100).toFixed(2)
      : 0;
  };

  const calculateTotal = (subTotal) => {
    const discount = calculateDiscount(subTotal);
    const serviceCharge = calculateServiceCharge(subTotal);
    const vat = calculateVat(subTotal);

    return (
      parseFloat(subTotal) +
      parseFloat(serviceCharge) +
      parseFloat(vat) -
      parseFloat(discount)
    ).toFixed(2);
  };

  const calculatePayAmount = (total, nonTaxableAmount) => {
    return (parseFloat(total) - parseFloat(nonTaxableAmount)).toFixed(2);
  };

  const subTotal = calculateSubTotal();
  const vat = calculateVat(subTotal);
  const discount = calculateDiscount(subTotal);
  const serviceCharge = calculateServiceCharge(subTotal);
  const total = calculateTotal(subTotal);
  const nonTaxableAmount = 0;
  const payAmount = calculatePayAmount(total, nonTaxableAmount);
  const totalPaidAmount = orders[0]?.invoice?.total_paid_amount || 0;
  const dueAmount = (parseFloat(total) - parseFloat(totalPaidAmount)).toFixed(
    2
  );
  const handlePrint = () => {
    const printContent = document.getElementById("print-container").innerHTML;
    const newWindow = window.open("", "", "width=1000,height=1000");
    newWindow.document.write(`
      <html>
        <head>
          <title>Receipt</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .PrintContainer { width: 100%; margin: 0; }
            .PrintHeader, .PrintBody { margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; }
            table, th, td { border: 1px solid; }
            th, td { padding: 8px; text-align: left; }
            .WebVissionss { display: none; }
            .BillNumber{width:100%; text-align:center;}
          </style>
        </head>
        <body>
          <div class="PrintContainer">${printContent}</div>
        </body>
      </html>
    `);
    newWindow.document.close();
    newWindow.print();
  };

  const handleSave = async () => {
    console.log("Order ID being sent: ", orderId); // Log to check if the order ID is correct
    try {
      const receiptData = {
        invoiceId: formData.invoiceId,
        subTotal,
        discount,
        serviceCharge,
        vat,
        total,
        payAmount,
      };
      const response = await axios.post(
        `${API_BASE_URL}/orders/createBill`,
        receiptData
      );
      if (response.data.status === "Success") {
        toast("Bill created successfully!");
        const ordersResponse = await axios.get(
          `${API_BASE_URL}/orders/get_order_by_table_slug/${slug}`
        );
        setOrders(ordersResponse.data.data || []);
      } else {
        toast(`Error: ${response.data.status_message}`);
      }
    } catch (error) {
      toast(`Error: ${error.message}`);
    }
  };

  const handlePay = () => {
    setFormData((prevState) => ({
      ...prevState,
      openPaymentDialog: true,
    }));
  };

  const isReceiptValid = subTotal > 0 && payAmount > 0;

  return (
    <>
      {orders.length === 0 ? (
        "No confirmed orders."
      ) : (
        <>
          <div className="PrintContainer" id="print-container">
            <div className="PrintHeader">
              <div className="BillNumber">
                <div className="CompanyInformation">
                  <h1>Company Name</h1>
                  <p>Kathmandu, Nepal</p>
                  <p>Phone No: 9865956061</p>
                </div>
                <p>
                  Bill No :{" "}
                  {orders?.map((order) => order.invoice.invoice_number)}
                </p>
                <p>Table No. : {tableId}</p>
              </div>
            </div>
            <div className="PrintBody">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product</th>
                    <th>Quantity</th>
                    <th>Rate</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 ? (
                    <tr>
                      <td colSpan="5">No confirmed orders.</td>
                    </tr>
                  ) : (
                    orders.map((order, orderIndex) =>
                      order.order_items.length === 0 ? (
                        <tr key={order.id}>
                          <td colSpan="5">No items for this order.</td>
                        </tr>
                      ) : (
                        order.order_items.map((item, itemIndex) => (
                          <tr key={item.id}>
                            <td>{itemIndex + 1}</td>
                            <td>{item.item_name}</td>
                            <td>{item.quantity}</td>
                            <td>{item.price}</td>
                            <td>{(item.quantity * item.price).toFixed(2)}</td>
                          </tr>
                        ))
                      )
                    )
                  )}
                </tbody>
              </table>
              <div className="WebVissionss">
                <div className="SubTotal">
                  <label>SubTotal: </label>
                  <input
                    type="text"
                    name="subTotal"
                    value={subTotal}
                    style={{ width: "150px", textAlign: "right" }}
                    readOnly
                    className="Amountssss"
                  />
                </div>
                <div className="Discount">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isDiscountOn}
                        onChange={() =>
                          setFormData((prevState) => ({
                            ...prevState,
                            isDiscountOn: !formData.isDiscountOn,
                          }))
                        }
                        name="discountSwitch"
                        color="primary"
                      />
                    }
                    label="Discount"
                    labelPlacement="start"
                    style={{ margin: 0 }}
                  />
                  {formData.isDiscountOn && (
                    <TextField
                      label="Discount in (%)"
                      type="number"
                      variant="outlined"
                      value={formData.discountPercent}
                      onChange={(e) =>
                        setFormData((prevState) => ({
                          ...prevState,
                          discountPercent: e.target.value,
                        }))
                      }
                      size="small"
                      style={{ width: 120 }}
                    />
                  )}
                  <input
                    type="text"
                    name="discount"
                    value={discount}
                    style={{ width: "150px", textAlign: "right" }}
                    readOnly
                    className="Amountssss"
                  />
                </div>
                <br />
                <div className="ServiceCharge">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isServiceChargeOn}
                        onChange={() =>
                          setFormData((prevState) => ({
                            ...prevState,
                            isServiceChargeOn: !formData.isServiceChargeOn,
                          }))
                        }
                        name="serviceChargeSwitch"
                        color="primary"
                      />
                    }
                    label="Service Charge"
                    labelPlacement="start"
                    style={{ margin: 0 }}
                  />
                  {formData.isServiceChargeOn && (
                    <TextField
                      label="Service Charge (%)"
                      type="number"
                      variant="outlined"
                      value={formData.serviceChargePercent}
                      onChange={(e) =>
                        setFormData((prevState) => ({
                          ...prevState,
                          serviceChargePercent: e.target.value,
                        }))
                      }
                      size="small"
                      style={{ width: 150 }}
                    />
                  )}
                  <input
                    type="text"
                    name="serviceCharge"
                    value={serviceCharge}
                    style={{ width: "150px", textAlign: "right" }}
                    readOnly
                    className="Amountssss"
                  />
                </div>
                <div className="NetAmount">
                  <label>Vat ({vatRate * 100}%): </label>
                  <input
                    type="text"
                    name="vat_percentage"
                    value={vat}
                    style={{ width: "150px", textAlign: "right" }}
                    readOnly
                    className="Amountssss"
                  />
                </div>
                <div className="NetAmount">
                  <label>Total: </label>
                  <input
                    type="text"
                    name="total"
                    value={total}
                    style={{ width: "150px", textAlign: "right" }}
                    readOnly
                    className="Amountssss"
                  />
                </div>
                <div className="NetAmount">
                  <label>Pay Amount: </label>
                  <input
                    type="text"
                    name="payAmount"
                    value={payAmount}
                    style={{ width: "150px", textAlign: "right" }}
                    readOnly
                    className="Amountssss"
                  />
                </div>
                <div className="NetAmount">
                  <label>Due Amount: </label>
                  <input
                    type="text"
                    value={dueAmount}
                    style={{ width: "150px", textAlign: "right" }}
                    readOnly
                    className="Amountssss"
                  />
                </div>
              </div>

              <table className="forPrint">
                <tr>
                  <td colSpan={4}>SubTotal</td>
                  <td>{subTotal}</td>
                </tr>
                <tr>
                  <td colSpan={4}>Discount</td>
                  <td>{discount}</td>
                </tr>
                <tr>
                  <td colSpan={4}>Service Charge</td>
                  <td>{serviceCharge}</td>
                </tr>
                <tr>
                  <td colSpan={4}>Vat (13%)</td>
                  <td>{vat}</td>
                </tr>
                <tr>
                  <td colSpan={4}> Grand Total</td>
                  <td>{total}</td>
                </tr>
                <tr>
                  <td colSpan={4}>Due Amount</td>
                  <td>{dueAmount}</td>
                </tr>
              </table>
            </div>
          </div>
          <div className="ActionsButton">
            <button onClick={handleSave}>Save</button>
            {orders.some((order) => order.invoice.isPrinted === "Y") && (
              <>
                <button onClick={handlePrint}>Print</button>
                <button onClick={handlePay}>Pay</button>
              </>
            )}
          </div>
        </>
      )}
      {/* Payment Dialog */}
      <Dialog
        open={formData.openPaymentDialog}
        onClose={() =>
          setFormData((prevState) => ({
            ...prevState,
            openPaymentDialog: false,
          }))
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Payment</DialogTitle>
        <DialogContent>
          <Payment
            invoiceId={formData.invoiceId}
            payAmount={payAmount}
            // dueAmount={dueAmount}
            dueAmount={parseFloat(dueAmount) || 0}
            table_id={formData.table_id}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setFormData((prevState) => ({
                ...prevState,
                openPaymentDialog: false,
              }))
            }
            color="secondary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default MakeReceipt;
